<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const schema = defineSchema({
	sections: $.array(
		'Sections',
		defineSchema({
			label: $.text('Nav Label'),
			title: $.text('Section Title'),
			content: $.richtext('Content'),
			link: $.text('External Link (optional)'),
		}),
		{
			collapsible: true,
			allowAdd: true,
			allowDelete: true,
			allowReorder: true,
			default: [
				{ label: 'About', title: 'About', content: '', link: '' },
				{ label: 'Staff', title: 'Staff', content: '', link: '' },
				{ label: 'Supporters', title: 'Supporters', content: '', link: '' },
				{ label: 'Our Values', title: 'Our Values', content: '', link: '' },
				{ label: 'Testimonials', title: 'Testimonials', content: '', link: '' },
				{ label: 'Authors', title: '', content: '', link: '/authors' },
				{ label: 'Submissions', title: 'Submissions', content: '', link: '' },
			],
		},
	),
	maxWidth: $.select('Max width', [...CONTENT_WIDTH_OPTIONS], { default: '1100' }),
	...pageHeightField(true),
})
export const BlockAbout = defineBlock({
	thumbnail: '/block-thumbs/BlockAbout.svg',
	category: 'Content',
	order: 15,
	name: 'BlockAbout',
	label: 'About (with side nav)',
	icon: 'menu_book',
	schema,
})
</script>
<script setup lang="ts">

import { Btn } from '@bagelink/vue'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
	CONTENT_WIDTH_OPTIONS,
	pageHeightClass,
	pageHeightField,
	widthClass,
} from '@/utils/blockLayout'
import RichHtml from '@/components/RichHtml.vue'



interface AboutSectionItem {
	label: string
	title?: string
	content?: string
	link?: string
}

const props = defineProps<{
	sections?: AboutSectionItem[]
	maxWidth?: string
	pageHeight?: boolean
}>()

const rootClass = computed(() =>
	[widthClass(props.maxWidth, '1100'), pageHeightClass(props.pageHeight ?? true)]
		.filter(Boolean)
		.join(' '),
)

// Only sections that render inline content (no external link) can be "active".
const contentSections = computed(() => (props.sections ?? []).filter(s => !s.link))

const activeIndex = ref(0)
const activeSection = computed(() => contentSections.value[activeIndex.value] ?? null)
const route = useRoute()

function sectionId(section: AboutSectionItem) {
	return (section.label || section.title || '')
		.toLowerCase()
		.replace(/[’']/g, '')
		.replace(/\s+/g, '-')
}

watch(
	() => [route.hash, contentSections.value] as const,
	([hash]) => {
		if (!hash) return
		const index = contentSections.value.findIndex(section => sectionId(section) === hash.slice(1))
		if (index >= 0) activeIndex.value = index
	},
	{ immediate: true },
)

function isActive(section: AboutSectionItem) {
	return !section.link && contentSections.value.indexOf(section) === activeIndex.value
}

function selectSection(section: AboutSectionItem) {
	if (section.link) return
	activeIndex.value = contentSections.value.indexOf(section)
}
</script>

<template>
	<div class="pb-3 pt-4 m_pt-2 m_w-100p m_px-05" :class="rootClass">
		<h1
			v-if="activeSection?.title"
			class="m-0 txt40 regular line-height-14 m_txt32 pb-2-5 txt-center m_txt-start m_pb-1 m_px-1"
		>
			{{ activeSection.title }}
		</h1>

		<div class="flex align-items-start gap-1 m_block m_overflow-hidden1">
			<nav
				class="sticky-3 h-auto bg-gray flex column py-1-5 px-2 m_bg-white m_row m_filters-mask m_overflow m_px-0 m_pb-0 m_border-bottom m_mb-1 m_top-3 m_pt-2"
			>
				<Btn
					v-for="section in props.sections"
					:key="section.label"
					class="txt17 py-1-5 w-100p m_w-auto m_flex-shrink-0 m_px-1"
					align-txt="start"
					flat
					:class="{ 'color-red': isActive(section) }"
					:to="section.link || undefined"
					@click="selectSection(section)"
				>
					{{ section.label }}
				</Btn>
			</nav>

			<RichHtml
				v-if="activeSection"
				:id="sectionId(activeSection)"
				class="about-section px-1 flex-grow"
				tag="section"
				:html="activeSection.content"
			/>
		</div>
	</div>
</template>

<style></style>
